import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { divisionService } from '../../services/division.service';
import { getErrorMessage } from '../../lib/api';
import type { Division, DivisionInput } from '../../types/crm';
import Loading from '../../components/Loading';

const divisionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  city: z.string().optional(),
  manager_name: z.string().optional(),
  sort_order: z.string().optional(),
});

type DivisionFormData = z.infer<typeof divisionSchema>;

export const Divisions = () => {
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Division | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DivisionFormData>({
    resolver: zodResolver(divisionSchema),
    defaultValues: {
      name: '',
      city: '',
      manager_name: '',
      sort_order: '0',
    },
  });

  useEffect(() => {
    loadDivisions();
  }, []);

  const loadDivisions = async () => {
    try {
      const result = await divisionService.getAll({ per_page: 100 });
      setDivisions(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    reset({ name: '', city: '', manager_name: '', sort_order: '0' });
    setShowModal(true);
  };

  const openEditModal = (division: Division) => {
    setEditing(division);
    reset({
      name: division.name,
      city: division.city ?? '',
      manager_name: division.manager_name ?? '',
      sort_order: String(division.sort_order),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    reset();
  };

  const onSubmit = async (data: DivisionFormData) => {
    setIsSaving(true);
    try {
      const payload: DivisionInput = {
        name: data.name,
        city: data.city || null,
        manager_name: data.manager_name || null,
        sort_order: data.sort_order ? Number(data.sort_order) : 0,
      };

      if (editing) {
        await divisionService.update(editing.id, payload);
        toast.success('Division updated');
      } else {
        await divisionService.create(payload);
        toast.success('Division created');
      }
      closeModal();
      loadDivisions();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (division: Division) => {
    if (!confirm(`Delete division "${division.name}"? Users assigned to it will be unassigned.`)) {
      return;
    }
    try {
      await divisionService.delete(division.id);
      toast.success('Division deleted');
      loadDivisions();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Divisions</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage org-chart units (Chicago, NYC, LA, etc.) used by sales planning.
          </p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add Division
        </button>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Manager
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sort
                </th>
                <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {divisions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                    No divisions yet. Add your first one to get started.
                  </td>
                </tr>
              ) : (
                divisions.map((division) => (
                  <tr key={division.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {division.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {division.city ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {division.manager_name ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {division.users_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {division.sort_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button
                        onClick={() => openEditModal(division)}
                        className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        aria-label={`Edit ${division.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(division)}
                        className="text-red-600 hover:text-red-900 inline-flex items-center"
                        aria-label={`Delete ${division.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closeModal}
            />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {editing ? 'Edit Division' : 'Add New Division'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="label">Name</label>
                      <input
                        {...register('name')}
                        id="name"
                        type="text"
                        className="input"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="city" className="label">City</label>
                      <input
                        {...register('city')}
                        id="city"
                        type="text"
                        className="input"
                      />
                    </div>

                    <div>
                      <label htmlFor="manager_name" className="label">Manager name</label>
                      <input
                        {...register('manager_name')}
                        id="manager_name"
                        type="text"
                        className="input"
                      />
                    </div>

                    <div>
                      <label htmlFor="sort_order" className="label">Sort order</label>
                      <input
                        {...register('sort_order')}
                        id="sort_order"
                        type="number"
                        className="input"
                      />
                      {errors.sort_order && (
                        <p className="mt-1 text-sm text-red-600">{errors.sort_order.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="btn btn-primary"
                  >
                    {isSaving ? 'Saving...' : editing ? 'Save changes' : 'Create division'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Divisions;
